<template>
  <div :class="classes" :style="styles" aria-label="Cargando contenido...">
    <div class="skeleton-shimmer" />
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  variant: {
    type: String,
    default: 'text',
    validator: (value) => ['text', 'title', 'subtitle', 'button', 'avatar', 'image', 'card', 'list', 'table'].includes(value)
  },
  width: {
    type: [String, Number],
    default: null
  },
  height: {
    type: [String, Number],
    default: null
  },
  rounded: {
    type: String,
    default: 'md',
    validator: (value) => ['none', 'sm', 'md', 'lg', 'xl', 'full'].includes(value)
  },
  lines: {
    type: Number,
    default: 1
  },
  animated: {
    type: Boolean,
    default: true
  }
})

// eslint-disable-next-line no-unused-vars
const classes = computed(() => {
  const classList = ['skeleton']

  classList.push(`skeleton-${props.variant}`)
  classList.push(`skeleton-rounded-${props.rounded}`)

  if (props.animated) classList.push('skeleton-animated')

  return classList
})

// eslint-disable-next-line no-unused-vars
const styles = computed(() => {
  const styleObj = {}

  if (props.width) {
    styleObj.width = typeof props.width === 'number' ? `${props.width}px` : props.width
  }

  if (props.height) {
    styleObj.height = typeof props.height === 'number' ? `${props.height}px` : props.height
  }

  return styleObj
})
</script>

<style scoped>
/* Base Skeleton */
.skeleton {
  position: relative;
  background: var(--color-gray-200);
  overflow: hidden;
  isolation: isolate;
}

.skeleton-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.6) 50%,
    transparent 100%
  );
  transform: translateX(-100%);
}

.skeleton-animated .skeleton-shimmer {
  animation: shimmer 1.5s infinite;
}

/* Variant Styles */
.skeleton-text {
  height: 1rem;
  width: 75%;
}

.skeleton-title {
  height: 1.5rem;
  width: 60%;
}

.skeleton-subtitle {
  height: 1.25rem;
  width: 80%;
}

.skeleton-button {
  height: 2.5rem;
  width: 8rem;
}

.skeleton-avatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: var(--radius-full);
}

.skeleton-image {
  width: 100%;
  height: 12rem;
}

.skeleton-card {
  width: 100%;
  height: 16rem;
}

.skeleton-list {
  width: 100%;
  height: 3rem;
}

.skeleton-table {
  width: 100%;
  height: 1.5rem;
}

/* Rounded Variants */
.skeleton-rounded-none { border-radius: var(--radius-none); }
.skeleton-rounded-sm { border-radius: var(--radius-sm); }
.skeleton-rounded-md { border-radius: var(--radius-md); }
.skeleton-rounded-lg { border-radius: var(--radius-lg); }
.skeleton-rounded-xl { border-radius: var(--radius-xl); }
.skeleton-rounded-full { border-radius: var(--radius-full); }

/* Shimmer Animation */
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* Dark Mode */
[data-theme="dark"] .skeleton {
  background: var(--color-gray-700);
}

[data-theme="dark"] .skeleton-shimmer {
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.1) 50%,
    transparent 100%
  );
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .skeleton-animated .skeleton-shimmer {
    animation: none;
  }
}

/* High Contrast */
@media (prefers-contrast: high) {
  .skeleton {
    background: var(--color-gray-400);
  }
}
</style>